import moment from 'moment-timezone';
import { fetchWithRetry } from '../utils/fetchHelper';

/**
 * Normaliza los datos de una transacción según su origen (V1 o V2)
 */
const normalizeTransactionData = (transaccion, accountTypeForConversion) => {
    let originalDate;

    if (accountTypeForConversion === "Cuenta Actual") {
        const dateStringWithoutZ = transaccion.date.endsWith('Z') ? transaccion.date.slice(0, -1) : transaccion.date;
        originalDate = moment.tz(dateStringWithoutZ, 'America/Mexico_City');
    } else {
        originalDate = moment.utc(transaccion.date);
    }

    const userLocalTime = originalDate.local();

    const normalized = {
        ...transaccion,
        date: userLocalTime.toISOString(),
    };

    // Asegurar que los campos numéricos sean floats absolutos (positivos)
    const fieldsToNormalize = ['total_amount', 'tip', 'subtotal', 'commission', 'vat', 'retention', 'total'];
    
    fieldsToNormalize.forEach(field => {
        if (normalized[field] !== undefined && normalized[field] !== null) {
            normalized[field] = Math.abs(parseFloat(normalized[field]) || 0);
        }
    });

    return normalized;
};

/**
 * Construye los parámetros de búsqueda para la API V1 (Cuenta Actual)
 */
const buildV1Params = (config) => {
    const { dateFrom, dateTo, page, limit, filters, knownMaskedCards } = config;
    let queryParams = [];
    queryParams.push(`dateFrom=${dateFrom.format('YYYY-MM-DDTHH:mm:ss')}`);
    queryParams.push(`dateTo=${dateTo.format('YYYY-MM-DDTHH:mm:ss')}`);
    queryParams.push(`size=${limit}`);
    queryParams.push(`page=${page}`);

    if (filters.transaccionId) queryParams.push(`transactionId=${filters.transaccionId}`);
    
    if (filters.ultimos4) {
        const fullCard = Array.from(knownMaskedCards || []).find(card => card && card.endsWith(filters.ultimos4));
        if (fullCard) {
            queryParams.push(`card=${fullCard}`);
        } else {
            // Valor centinela para forzar búsqueda vacía si no conocemos la tarjeta
            queryParams.push(`card=000000******0000`);
            return { error: 'CARD_NOT_FOUND', query: null };
        }
    }

    if (filters.devices?.length > 0) queryParams.push(`device=${filters.devices.join(',')}`);
    
    if (filters.estado?.length > 0) {
        if (filters.estado.includes('Devolucion')) {
            queryParams.push(`transactionType=devolucion`);
            queryParams.push(`transactionStatus=aprobada`);
        } else if (filters.estado.includes('Aprobada')) {
            queryParams.push(`transactionStatus=aprobada`);
            queryParams.push(`transactionType=venta`);
        }
        // Nota: 'Rechazada' se filtra localmente en V1 usualmente
    }

    return { error: null, query: queryParams.join('&') };
};

/**
 * Construye los parámetros de búsqueda para la API V2 (Cuentita / Concentradora)
 */
const buildV2Params = (config) => {
    const { dateFrom, dateTo, page, limit, filters } = config;
    let queryParams = [];
    queryParams.push(`from=${dateFrom.format()}`);
    queryParams.push(`to=${dateTo.format()}`);
    queryParams.push(`limit=${limit}`);
    queryParams.push(`page=${page}`);

    if (filters.transaccionId) queryParams.push(`transaction_id=${filters.transaccionId}`);
    
    if (filters.ultimos4) {
        queryParams.push(`masked_card=${filters.ultimos4}`);
    }

    if (filters.devices?.length > 0) queryParams.push(`device=${filters.devices.join(',')}`);

    if (filters.estado?.length > 0) {
        if (filters.estado.includes('Devolucion')) {
            queryParams.push(`transaction_type=devolucion`);
            queryParams.push(`status=aprobada`);
        } else {
            const apiStatuses = filters.estado.map(s => {
                if (s === 'Aprobada') return 'aprobada';
                if (s === 'Rechazada') return 'rechazada';
                return s.toLowerCase();
            });
            queryParams.push(`status=${apiStatuses.join(',')}`);
            if (filters.estado.includes('Aprobada') || filters.estado.includes('Rechazada')) {
                queryParams.push(`transaction_type=venta`);
            }
        }
    }

    if (filters.tipo?.length > 0) {
        queryParams.push(`card_type=${filters.tipo.join(',')}`);
    }

    return { error: null, query: queryParams.join('&') };
};

/**
 * Función principal para obtener transacciones
 */
export const fetchTransactions = async (config) => {
    const { 
        accessToken, bpToken, accountType, 
        page, limit, filters, 
        fechaInicio, fechaFin, horaInicio, horaFin,
        knownMaskedCards 
    } = config;
    
    const apiUrl = process.env.REACT_APP_API_URL_PROD;
    let requestUrl;
    let headers = { 'authorization': accessToken };
    let dateFrom, dateTo;

    // 1. Manejo de Fechas
    if (fechaInicio && fechaFin) {
        const startH = horaInicio ? horaInicio.hour() : 0;
        const startM = horaInicio ? horaInicio.minute() : 0;
        const endH = horaFin ? horaFin.hour() : moment().hour();
        const endM = horaFin ? horaFin.minute() : moment().minute();

        const localFrom = moment(`${fechaInicio.format("YYYY-MM-DD")} ${startH}:${startM}`, "YYYY-MM-DD HH:mm");
        const localTo = moment(`${fechaFin.format("YYYY-MM-DD")} ${endH}:${endM}`, "YYYY-MM-DD HH:mm");

        if (accountType === "Cuenta Actual") {
            dateFrom = localFrom.tz('America/Mexico_City');
            dateTo = localTo.tz('America/Mexico_City');
        } else {
            dateFrom = localFrom.utc();
            dateTo = localTo.utc();
        }
    } else {
        // Fechas por defecto
        if (accountType === "Cuenta Actual") {
            dateFrom = moment().subtract(5, 'months').startOf('day').tz('America/Mexico_City');
            dateTo = moment.tz('America/Mexico_City');
        } else {
            dateFrom = moment.utc('2025-04-27T00:00:00');
            dateTo = moment.utc();
        }
    }

    // 2. Construcción de URL según API
    if (accountType === "Cuenta Actual") {
        if (!bpToken) throw new Error('MISSING_BP_TOKEN');
        headers['bp-token'] = bpToken;
        
        const v1 = buildV1Params({ dateFrom, dateTo, page, limit, filters, knownMaskedCards });
        if (v1.error === 'CARD_NOT_FOUND') return { error: 'CARD_NOT_FOUND', data: [] };
        
        requestUrl = `${apiUrl}/transactions?${v1.query}`;
    } else {
        const v2 = buildV2Params({ dateFrom, dateTo, page, limit, filters });
        requestUrl = `${apiUrl}/api/v2/transactions?${v2.query}`;
    }

    // 3. Ejecución del Fetch con reintento
    const response = await fetchWithRetry(requestUrl, { method: 'GET', headers });

    if (response.status === 401) return { error: 'UNAUTHORIZED', status: 401 };
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404 && (errorData.total_items === 0 || errorData.total === 0)) {
            return { data: [], totalItems: 0, totalPages: 0 };
        }
        throw new Error(`API_ERROR_${response.status}`);
    }

    const data = await response.json();
    
    // 4. Normalización de Resultados
    const rawList = data.data || [];
    const normalizedList = rawList.map(t => normalizeTransactionData(t, accountType));

    // 5. Metadatos de paginación
    let totalItems = 0;
    let totalP = 1;

    if (accountType === "Cuenta Actual") {
        totalItems = data.total || 0;
        totalP = Math.ceil(totalItems / limit) || 1;
    } else {
        totalItems = data.total_items || 0;
        const perPage = data.content_per_page || limit;
        totalP = Math.ceil(totalItems / perPage) || 1;
    }

    return {
        data: normalizedList,
        totalItems,
        totalPages: totalP
    };
};

/**
 * Obtiene dispositivos únicos registrados para el comercio
 * Realiza dos búsquedas: una histórica y una reciente para maximizar la cobertura.
 */
export const fetchUniqueDevices = async (config) => {
    const { 
        accessToken, bpToken, accountType,
        itemsPerPage, itemsPerPageC
    } = config;

    const apiUrl = process.env.REACT_APP_API_URL_PROD;   
    
    // Función auxiliar interna para fetch por rangos
    const fetchDevicesInRange = async (dateFrom, dateTo, maxPages = 10, currentAT, currentBT) => {
        let allTransactions = [];
        let currentPage = 1;
        let totalPages = 1;
        let internalAT = currentAT;
        let internalBT = currentBT;

        do {
            let requestUrl;
            let headers = { 'authorization': internalAT };
            const limit = accountType === "Cuenta Actual" ? itemsPerPage : itemsPerPageC;

            if (accountType === "Concentradora" || accountType === "Cuentita") {
                const dateFromISO = moment.utc(dateFrom).format();
                const dateToISO = moment.utc(dateTo).format();
                requestUrl = `${apiUrl}/api/v2/transactions?from=${dateFromISO}&to=${dateToISO}&limit=${itemsPerPageC}&page=${currentPage}`;
            } else {
                if (!internalBT) return { error: 'MISSING_BP_TOKEN' };
                const dateFromISO = moment.tz(dateFrom, 'America/Mexico_City').format('YYYY-MM-DDTHH:mm:ss');
                const dateToISO = moment.tz(dateTo, 'America/Mexico_City').format('YYYY-MM-DDTHH:mm:ss');
                requestUrl = `${apiUrl}/transactions?dateFrom=${dateFromISO}&dateTo=${dateToISO}&size=${itemsPerPage}&page=${currentPage}`;
                headers['bp-token'] = internalBT;
            }

            const response = await fetchWithRetry(requestUrl, { method: 'GET', headers });

            if (response.status === 401) {
                return { error: 'UNAUTHORIZED', status: 401 };
            }

            if (response.status === 404) {
                console.warn(`No transactions found for range (404): ${requestUrl}`);
                return { data: [] };
            }

            if (!response.ok) throw new Error(`DEVICES_FETCH_ERROR_${response.status}`);

            const data = await response.json();
            const fetched = data.data || [];
            allTransactions = allTransactions.concat(fetched);

            if (currentPage === 1) {
                const totalItems = data.total || data.total_items || 0;
                totalPages = Math.ceil(totalItems / limit);
            }

            currentPage++;
        } while (currentPage <= totalPages && currentPage <= maxPages);

        return { data: allTransactions };
    };

    // Búsqueda 1: Período histórico
    const historicalFrom = accountType === "Cuenta Actual" 
        ? moment.tz('2025-07-01T00:00:00', 'America/Mexico_City').toDate()
        : moment.utc('2025-07-01T00:00:00').toDate();
    const historicalTo = accountType === "Cuenta Actual"
        ? moment.tz('2025-07-20T23:59:59', 'America/Mexico_City').toDate()
        : moment.utc('2025-07-20T23:59:59').toDate();

    // Búsqueda 2: Últimos 60 días
    const recentTo = accountType === "Cuenta Actual" ? moment.tz('America/Mexico_City').toDate() : moment.utc().toDate();
    const recentFrom = accountType === "Cuenta Actual"
        ? moment.tz('America/Mexico_City').subtract(60, 'days').startOf('day').toDate()
        : moment.utc().subtract(60, 'days').startOf('day').toDate();

    try {
        const results = await Promise.allSettled([
            fetchDevicesInRange(historicalFrom, historicalTo, 5, accessToken, bpToken),
            fetchDevicesInRange(recentFrom, recentTo, 3, accessToken, bpToken)
        ]);

        let allTransactions = [];
        let unauthorized = false;

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                const val = result.value;
                if (val.error === 'UNAUTHORIZED') {
                    unauthorized = true;
                } else if (val.data) {
                    allTransactions = allTransactions.concat(val.data);
                }
            } else {
                console.error(`Range search ${index} failed:`, result.reason);
            }
        });

        if (unauthorized) return { error: 'UNAUTHORIZED' };

        const uniqueDevices = [...new Set(allTransactions.map(t => t.device).filter(Boolean))];
        uniqueDevices.sort((a, b) => a.localeCompare(b));

        return { 
            data: uniqueDevices.map(device => ({ value: device, label: device }))
        };

    } catch (err) {
        console.error('Error in fetchUniqueDevices service:', err);
        throw err;
    }
};
